import { animate, style, transition, trigger } from '@angular/animations';
import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Host, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { LoadingSnackbarService, SnackMessageNames, SnackMessageText } from '../../../../shared/services/loading-snackbar.service';
import { UtilsService } from '../../../../shared/services/utils.service';
import { DataRulesConfirmDialogComponent } from '../../../data-rules-confirm-dialog/data-rules-confirm-dialog.component';
import { DataRulesDataService } from '../../../data-rules-data.service';
import { AllPrimaryEntitiesNames, DataRuleId } from '../../../data-rules-model';
import { ISavedDataRuleEventData } from '../../../data-rules.component';
import { PairedRuleAutocompleteComponent } from '../../../lib/paired-rule-autocomplete/paired-rule-autocomplete.component';
import { transitionNewHeightInOut, transitionNewWidthInOut } from '../../kyloe-animations';
import { ScheduleDataService } from '../../rule-schedule/schedule-data.service';
import { IChainViewState } from '../chained-rules-model';
import { ChainData, ComplexChainData, RuleChainsDataService } from '../rule-chain-data.service';

function isRulePickedCustomValidator (): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as DataRuleId;

    return !value || new UtilsService().compareInternalDataRuleIds(value, 0) ? {rule: 'Rule must be picked'} :  null;
  };
}

@Component({
  selector: 'app-rule-chain',
  templateUrl: './chained-rules-group.component.html',
  styleUrls: ['./chained-rules-group.component.scss'],
  animations: [
    trigger('transitionNewRuleInOut', [
      transition(':enter', [
        style({ height: '0' }),
        animate('.15s ease-out', style({ height: '53px' })),
      ]),
      transition(':leave', [
        style({ height: '53px' }),
        animate('.15s ease-out', style({ height: '0' })),
      ]),
    ]),
    transitionNewWidthInOut, transitionNewHeightInOut
  ],
  imports: [
    ReactiveFormsModule,
    NgClass,
    NgIf,
    PairedRuleAutocompleteComponent,
    NgForOf,
    CdkDropList,
    MatTooltip,
    CdkDrag
  ]
})
export class ChainedRulesGroupComponent implements OnInit, AfterViewInit {

  chainForm: FormGroup;
  rules: FormArray;
  showNameInput = false;
  isLoading: boolean;
  chainSaved = true;
  @Input() chainData: ComplexChainData;
  @Input() savedChainEvent: Subject<ISavedDataRuleEventData>;
  @Input() onChainDeleted: (id: DataRuleId) => void;
  @Input() navigateToDataRules: (entity: AllPrimaryEntitiesNames, rules?: DataRuleId[]) => void;

  @Input() parentGetViewState: (chainId: DataRuleId) => IChainViewState;
  @Input() parentSetViewState: (chainId: DataRuleId, state: IChainViewState) => void;

  @Output() chainClickedEventEmitter = new EventEmitter<DataRuleId>();
  @ViewChild('focusElement') focusElement: ElementRef;

  constructor(public ruleChainsDataService: RuleChainsDataService,
              public utilsService: UtilsService,
              public chainReference: ElementRef,
              private loadingSnackbarService: LoadingSnackbarService,
              private formBuilder: FormBuilder,
              private dataRulesDataService: DataRulesDataService,
              private dialog: MatDialog,
              private scheduleDataService: ScheduleDataService,
  ) {
  }

  ngOnInit(): void {
    this.showNameInput = this.chainData.name.length === 0;
    this.buildForm();
  }

  ngAfterViewInit(): void {
    if (this.showNameInput)
      this.focusElement.nativeElement.focus();
  }

  public get viewState(): IChainViewState {
    return this.parentGetViewState(this.chainData.id);
  }

  private setViewState(props: { expanded?: boolean, dirty?: boolean }): void {
    this.parentSetViewState(this.chainData.id, Object.assign(this.viewState, props));
  }

  saveChainGroup(chainName: string) {
    this.chainSaved = false;
    const chain: ChainData = {
      id: this.chainForm.value.id,
      name: this.chainForm.value.name,
      ruleIds: this.chainData.rules.map(rule => rule.id as string)
    };

    const allSchedules = this.scheduleDataService.allSchedules;
    const scheduleInChain = allSchedules.find(schedule => (
      chain.ruleIds.findIndex(ruleId => ruleId === schedule.dataRulesID) > -1
    ));
    const scheduledRuleIndex = !!scheduleInChain
      ? chain.ruleIds.findIndex(ruleId => ruleId === scheduleInChain.dataRulesID)
      : -1;

    // required swapping of rule schedules to keep chain be containing a schedule for first rule in steps
    if (!!scheduleInChain && scheduledRuleIndex > 0) {
      const ruleToAssignSchedule = this.dataRulesDataService.getRule(chain.ruleIds[0]);
      const scheduledRule = this.dataRulesDataService.getRule(chain.ruleIds[scheduledRuleIndex]);
      const dialogRef = this.dialog.open(DataRulesConfirmDialogComponent, {
        width: '45vw',
        height: 'auto',
        autoFocus: true,
        data: {
          titleIcon: 'bhi-delete-o',
          title: chainName,
          messageIcon: 'playlist_play',
          message: `${scheduledRule.name} rule's schedule will be reassigned to ${ruleToAssignSchedule.name} rule`
        }
      });

      dialogRef.afterClosed().subscribe((data) => {
        if (!data)
          return;

        this.scheduleDataService.swapScheduleBetweenRules(scheduledRule.id as string, ruleToAssignSchedule.id as string).subscribe(() => {
          this.loadingSnackbarService.show(SnackMessageNames.CHAIN_SAVED, `${SnackMessageText.CHAIN_SAVED} ${chainName}`);
          this.ruleChainsDataService.saveChain(chain).subscribe((resp) => {
            console.log(resp);
            this.chainSaved = true;
            this.loadingSnackbarService.hide(SnackMessageNames.CHAIN_SAVED);
            this.setViewState({dirty: false});
            this.savedChainEvent.next({unsavedId: chain.id, savedId: resp});
          });
        });
      });
    } else {
      this.loadingSnackbarService.show(SnackMessageNames.CHAIN_SAVED, `${SnackMessageText.CHAIN_SAVED} ${chainName}`);
      this.ruleChainsDataService.saveChain(chain).subscribe((resp) => {
        console.log(resp);
        this.chainSaved = true;
        this.loadingSnackbarService.hide(SnackMessageNames.CHAIN_SAVED);
        this.setViewState({dirty: false});
        this.savedChainEvent.next({unsavedId: chain.id, savedId: resp});
      });
    }
  }

  buildForm() {

    this.rules = this.formBuilder.array([], [Validators.required, Validators.minLength(1)]);
    for (let i = 0; i < this.chainData.rules.length; i++) {
      const control = this.formBuilder.control(this.chainData.rules[i], [isRulePickedCustomValidator()]);
      control.valueChanges.subscribe(this.newRuleSelected.bind(this));
      this.rules.push(control);
    }

    this.chainForm = this.formBuilder.group(
      {
        id: [this.chainData.id],
        name: [this.chainData.name, Validators.required],
        rules: this.rules
      }
    );
    this.chainForm.valueChanges.subscribe(_ => {
      this.setViewState({dirty: this.chainForm.dirty});
    });

  }

  get rulesForms() {
    return this.chainForm.get('rules') as FormArray;
  }

  getDisabledRulesFunc(): Observable<string[]> {
    return forkJoin({
      chainedRules: this.ruleChainsDataService.getRulesThatInChainsData(),
      schedules: of(this.scheduleDataService.allSchedules)
    }).pipe(
      map(({chainedRules, schedules}) => {
        let resultRestrictedRules: string[] = [...chainedRules.map(ruleInChain => ruleInChain.id)];

        // allow scheduled rules only if chain doesn't have any scheduled rules already
        const alreadyScheduledRuleIndex = this.chainData.rules
          .findIndex(rule => schedules
            .findIndex(schedule => schedule.dataRulesID === rule.id) > -1
          );
        if (alreadyScheduledRuleIndex > -1) {
          const additionalScheduledRulesToRestrict = schedules
            .map(schedule => schedule.dataRulesID)
            .filter(dataRuleID => resultRestrictedRules.findIndex(restrictedRuleID => restrictedRuleID === dataRuleID) === -1);
          resultRestrictedRules = resultRestrictedRules.concat(additionalScheduledRulesToRestrict);
        }

        return resultRestrictedRules;
      })
    );
  }

  addNewRule() {
    const rule = this.formBuilder.control(0, isRulePickedCustomValidator());
    rule.valueChanges.subscribe(this.newRuleSelected.bind(this));
    this.rulesForms.push(rule);
    this.rulesForms.markAsDirty();
    this.setViewState({dirty: true});
  }

  deleteRule(i: number) {
    this.ruleChainsDataService.removeRuleFromChain(this.chainData.id, i);
    this.rulesForms.removeAt(i);
    this.rulesForms.markAsDirty();
    this.setViewState({dirty: true});
  }

  deleteGroup(chainName?: string): void {
    const dialogRef = this.dialog.open(DataRulesConfirmDialogComponent, {
      width: '45vw',
      height: 'auto',
      autoFocus: true,
      data: {
        titleIcon: 'bhi-delete-o',
        title: 'Are you sure you want to delete this chain?',
        messageIcon: 'playlist_play',
        message: chainName
      }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (!data)
        return;

      this.ruleChainsDataService.deleteChain(this.chainData.id).subscribe(() => {
        this.onChainDeleted(this.chainData.id);
      });
    });
  }

  drop(event: CdkDragDrop<string[]>): void {
    this.ruleChainsDataService.moveRule(this.chainData.id, event.previousIndex, event.currentIndex);
    this.rulesForms.markAsDirty();
    this.setViewState({dirty: true});
  }

  get name(): string {
    return this.chainForm.get('name').value;
  }

  onNameBlur(): void {
    this.showNameInput = this.name.length === 0;
  }

  onNameClick(): void {
    this.showNameInput = true;
  }

  onNameMouseOver(target): void {
    target.focus();
  }

  expandCollapseRule(id: DataRuleId) {
    this.chainClickedEventEmitter.emit(id);
  }

  newRuleSelected(ruleId: string) {
    if (!this.utilsService.compareInternalDataRuleIds(ruleId, 0)) {
      this.isLoading = true;
      this.ruleChainsDataService.addRule(this.chainData.id, ruleId);
      this.chainForm.markAsDirty();
      this.setViewState({dirty: true});
      this.isLoading = false;
    }
  }

  renderIcon(entity: string): string {
    entity = this.utilsService.parseCamelCaseToHyphenDivided(entity);
    const defaultNames = () => {
      if (entity.includes('candidate'))
        return 'bhi-candidate';
      if (entity.includes('contact') || entity.includes('person'))
        return 'bhi-person';
      if (entity.includes('client-corporation'))
        return 'bhi-company';
      if (entity.includes('submission'))
        return 'bhi-job submission';
      if (entity.includes('job'))
        return 'bhi-job';
      if (entity.includes('lead'))
        return 'bhi-lead';
      if (entity.includes('opportunity'))
        return 'bhi-opportunity';
      if (entity.includes('placement'))
        return 'bhi-star';
      if (entity.includes('appointment'))
        return 'bhi-appointment';
      if (entity.includes('task'))
        return 'bhi-check-o';
      if (entity.includes('note'))
        return 'bhi-add-note';
      return 'bhi-puzzle';
    };
    const defaultName = defaultNames();
    return entity.includes('custom-object')
    // todo hardcoded
    || entity === 'person'
      ? `${defaultName} custom-object` : defaultName;
  }

}
